// Appels serveur du module « Conseillère IA » (functions/api/conseillere.js) : réglages de l'assistante
// virtuelle et boîte des conversations. Cadrage : docs/CADRAGE-MODULE-IA-CONSEILLERE.md.
import { apiFetch } from './api';

export type ConversationStatut = 'ia' | 'attente_humain' | 'humain' | 'fermee';
export type MessageAuteur = 'visiteur' | 'ia' | 'humain' | 'systeme';

export interface ConseillereReglages {
    enabled: boolean;
    prenom: string;
    presentation: string;
    ton: 'vous' | 'tu';
    options: { faq: boolean; devis: boolean; rdv: boolean };
    consignes: string;
    faq: string;
    couleur: string;
    quota_mois: number;
    /** Façon d'afficher la conversation : bulle flottante, fenêtre au centre, ou conversation ouverte dans la page. */
    widget_style: 'bulle' | 'modale' | 'integre';
    /** Nom du bouton (ex. « Une question ? ») ; vide = « Discuter avec <prénom> ». */
    bouton_libelle: string;
    position: 'droite' | 'gauche';
    /** Genre de l'assistant(e) : accorde ses phrases et les textes affichés. */
    genre: 'f' | 'm';
    /** Décoché : le bouton de conversation disparaît du site et de Ma Carte, sans toucher au code installé. */
    affichee: boolean;
    /** Message d'information « vous parlez à une IA » propre à l'établissement ; vide = celui du super-admin. */
    mention_ia: string;
    /** Moyens de paiement que l'établissement déclare accepter (la conseillère ne lit pas son compte Stripe). */
    moyens_paiement: string[];
    /** E-mails envoyés au visiteur : envoyé ou non, objet, introduction, mot de la fin (et message de fin de conversation pour le rendez-vous). */
    emails: Record<'devis_valide' | 'demande_devis' | 'rdv_confirme' | 'rdv_attente', EmailReglage>;
    /** Rendez-vous choisi dans la conversation : « immediat » (confirmé selon le planning) ou « attente » (l'établissement le confirme ensuite). */
    rdv_confirmation: 'immediat' | 'attente';
}

export interface EmailReglage { actif: boolean; sujet: string; intro: string; fin: string; chat?: string }

export interface ConseillereUsage { messages: number; tokens_in: number; tokens_out: number; conversations: number; conversations_equipe: number }

export interface ConseillereConversation {
    id: string;
    status: ConversationStatut;
    source: string;
    name: string;
    email: string;
    phone: string;
    handoff_reason: string;
    dossier_id: string;
    unread: number;
    created_at: string;
    last_at: string;
    closed_at: string;
    apercu: string;
    apercu_auteur: string;
}

export interface ConseillereMessage {
    seq: number; author: MessageAuteur; body: string; created_at: string;
    /** Cartes de choix proposées au visiteur (message de la conseillère) : voir conseillere-devis.js. */
    payload?: string;
}

export const conseillereApi = {
    settings: (): Promise<{ settings: ConseillereReglages; usage: ConseillereUsage; options_disponibles: string[]; quota_defaut: number; devis: { possible: boolean; raison?: string; minimum_ttc?: number }; devis_pro?: { possible: boolean; raison?: string }; depassement: InfosDepassement; cle_test: string; essais: { conversations: number; messages: number; limite_conversations: number; limite_messages: number; restants: number }; mention: { defaut: string; modifiable: boolean; affichee: string } }> =>
        apiFetch('/conseillere-ia/settings'),
    testEmail: (type: string, config: Partial<EmailReglage>): Promise<{ ok: boolean; destinataire: string; titre: string }> =>
        apiFetch('/conseillere-ia/settings/email-test', { method: 'POST', body: JSON.stringify({ type, config }) }),
    saveDepassement: (actif: boolean): Promise<InfosDepassement> => apiFetch('/conseillere-ia/settings/depassement', { method: 'PUT', body: JSON.stringify({ actif }) }),
    saveSettings: (payload: Partial<ConseillereReglages>): Promise<{ settings: ConseillereReglages }> =>
        apiFetch('/conseillere-ia/settings', { method: 'PUT', body: JSON.stringify(payload) }),
    badge: (): Promise<{ en_attente: number }> => apiFetch('/conseillere-ia/badge'),
    conversations: (statut: 'attente' | 'toutes'): Promise<{ conversations: ConseillereConversation[] }> =>
        apiFetch(`/conseillere-ia/conversations?statut=${statut}`),
    conversation: (id: string): Promise<{ conversation: ConseillereConversation; messages: ConseillereMessage[] }> =>
        apiFetch(`/conseillere-ia/conversations/${id}`),
    reply: (id: string, body: string): Promise<{ ok: boolean; message: ConseillereMessage; courriel: string }> =>
        apiFetch(`/conseillere-ia/conversations/${id}/reply`, { method: 'POST', body: JSON.stringify({ body }) }),
    rendreIa: (id: string) => apiFetch(`/conseillere-ia/conversations/${id}/rendre-ia`, { method: 'POST' }),
    close: (id: string) => apiFetch(`/conseillere-ia/conversations/${id}/close`, { method: 'POST' }),
    remove: (id: string) => apiFetch(`/conseillere-ia/conversations/${id}`, { method: 'DELETE' }),
};

/** Tarif Workers AI (envoyé par le serveur, jamais recopié ici) : gratuité quotidienne du compte, puis prix des neurones. */
export interface ConseillereTarif {
    neuronesGratuitsParJour: number;
    dollarsPar1000Neurones: number;
    neuronesParMillionEntree: number;
    neuronesParMillionSortie: number;
    eurosParDollar: number;
}

/** Un client dans la synthèse des coûts : sa consommation, ce qu'elle coûte vraiment, et sa marge. */
export interface ConseillereLigneClient {
    client_id: string;
    nom: string;
    actif: boolean;
    quota_mois: number;
    quota_effectif: number;
    prix_mensuel_eur: number;
    messages: number;
    conversations: number;
    conversations_equipe: number;
    neurones: number;
    neurones_avis: number;
    cout_plein_usd: number;
    cout_reel_usd: number;
    marge_eur: number | null;
}

export interface ConseillereSyntheseCouts {
    mois: string;
    tarif: ConseillereTarif;
    jours: { jour: string; neurones: number; gratuits: number; payants: number; cout_reel_usd: number; payant: boolean }[];
    clients: ConseillereLigneClient[];
    totaux: {
        messages: number; conversations: number; conversations_equipe: number; neurones: number; neurones_gratuits: number; neurones_payants: number;
        cout_plein_usd: number; cout_reel_usd: number; jours_payants: number; revenu_eur: number; marge_eur: number;
    };
}

export interface ConseillereAdminEtat {
    quota_mois: number;
    quota_defaut: number;
    quota_effectif: number;
    prix_mensuel_eur: number;
    enabled: boolean;
    mois: { mois: string; messages: number; tokens_in: number; tokens_out: number; cout_plein_usd: number }[];
    mois_en_cours: Partial<ConseillereLigneClient> & { tarif: ConseillereTarif; jours_payants: number };
}

export interface ConseillereGlobaux { mention_ia_defaut: string; mention_modifiable: boolean }

/** Espace super-admin : l'offre d'un client (messages inclus, prix facturé), sa consommation et ce que ça coûte vraiment. */
export interface AlerteGratuitIa { jour: string; neurones: number; limite: number; pourcent: number; niveau: 0 | 1 | 2 }

/** Continuer au-delà des conversations incluses, au prix de chaque conversation supplémentaire (le client le choisit lui-même). */
export interface InfosDepassement { propose: boolean; actif: boolean; prix_eur: number; quota: number; supplementaires: number; montant_eur: number }

export const conseillereAdminApi = {
    alerte: (): Promise<AlerteGratuitIa> => apiFetch('/admin/conseillere/alerte'),
    get: (clientId: string): Promise<ConseillereAdminEtat> => apiFetch(`/admin/conseillere/${clientId}`),
    saveOffre: (clientId: string, quota_mois: number, prix_mensuel_eur: number) =>
        apiFetch(`/admin/conseillere/${clientId}`, { method: 'PUT', body: JSON.stringify({ quota_mois, prix_mensuel_eur }) }),
    globaux: (): Promise<ConseillereGlobaux> => apiFetch('/admin/conseillere/globaux'),
    saveGlobaux: (payload: Partial<ConseillereGlobaux>): Promise<ConseillereGlobaux> =>
        apiFetch('/admin/conseillere/globaux', { method: 'PUT', body: JSON.stringify(payload) }),
    couts: (mois?: string): Promise<ConseillereSyntheseCouts> => apiFetch(`/admin/conseillere${mois ? `?mois=${mois}` : ''}`),
};
